import type {
  BasePollingService,
  LbEventVarInfo,
  LbEventPhase,
  LbOptions,
  LbPart,
  LbPost,
  LbPostData,
} from '@uefacom-newdev/liveblog-integration-react';
import { getPostTypes, LbEventSubType, LbEventType, PartType } from '@uefacom-newdev/liveblog-integration-react';

import type { LbIcon } from './matchEventIcons';
import { matchEventIcons, varIcons } from './matchEventIcons';

import type { MediaImage } from '@cobalt/utilities';
import { getImage } from '@cobalt/utilities';
import type { Match } from '@shared-types/match.types';
import { loadFromAPIs } from '@utilities/fetch';
import { removeDiacritics } from '@utilities/text-manipulation';
import { uefaBaseUrl } from '@utilities/WindowContext';

export const getMatchEventIcon = (type: LbEventType, subType: LbEventSubType | LbEventPhase): LbIcon =>
  matchEventIcons[type]?.[subType] ?? matchEventIcons[type];

export const getVarEventIcon = ({ status }: LbEventVarInfo): LbIcon => ({
  iconName: 'football-var',
  iconColor: varIcons[status],
});

export type LiveblogStarReporter = {
  type: 'star';
  person: string;
  description: string;
  image: MediaImage['image'];
  imageUrl: string;
};

export const getStarReporter = (mediaItem): LiveblogStarReporter => {
  const figure = mediaItem?.figure?.[0];

  if (!figure?.img) return null;

  const image = getImage(mediaItem)?.image ?? {};

  return {
    person: figure.person,
    description: figure.description,
    type: 'star',
    image: image,
    imageUrl: image?.square?.s4,
  };
};

export type LiveBlog = {
  liveblogData?: {
    lbHomeReporters?: Record<string, string>[];
    lbAwayReporters?: Record<string, string>[];
    lbNeutralReporters?: Record<string, string>[];
    lbType?: string;
  };
  document?: {
    [key: string]: unknown;
    content?: {
      _type?: string;
      [key: string]: string;
    }[];
  };
};

export type LiveblogReporter = {
  id: string;
  type: 'home' | 'away' | 'neutral' | 'star';
  role: string;
  displayName: string;
  imageUrl: string;
};

const getReporter =
  (type: 'Home' | 'Away' | 'Neutral') =>
  (item: Record<string, string>): LiveblogReporter => ({
    id: item[`lb${type}ReporterId`],
    type: type.toLowerCase() as 'home' | 'away' | 'neutral',
    role: item[`lb${type}ReporterRole`],
    displayName: item[`lb${type}ReporterName`],
    imageUrl:
      item.lbReporterImage ??
      `${window.imgBaseUrl}/imgml/correspondants/eidos/${removeDiacritics(item[`lb${type}ReporterId`])}.png`,
  });

export const getLiveblogData = (liveblog: LiveBlog) => {
  const homeReporters: LiveblogReporter[] = liveblog?.liveblogData?.lbHomeReporters?.map(getReporter('Home'));
  const awayReporters: LiveblogReporter[] = liveblog?.liveblogData?.lbAwayReporters?.map(getReporter('Away'));
  const neutralReporters: LiveblogReporter[] = liveblog?.liveblogData?.lbNeutralReporters?.map(getReporter('Neutral'));
  const type = liveblog?.liveblogData?.lbType ?? '';

  const starReporters: LiveblogStarReporter[] =
    liveblog?.document?.content
      ?.filter((o) => o._type === 'AMBASSADOR-PHOTO')
      .map(getStarReporter)
      .filter(Boolean) ?? [];

  return {
    homeReporters,
    awayReporters,
    neutralReporters,
    starReporters,
    type,
  };
};

export type LiveblogData = ReturnType<typeof getLiveblogData>;

/**
 * Get post home/away/neutral kind
 */
export function getPostHomeAway(
  match: Match,
  liveblogPostData: LbPostData,
  reporter?: LiveblogReporter | LiveblogStarReporter,
): 'home' | 'away' | 'neutral' | 'star' {
  let ret: 'home' | 'away' | 'neutral' = 'neutral';

  if (reporter && reporter.type !== 'neutral') return reporter.type;
  if (match) {
    const event = liveblogPostData?.lbPostFSPEvent;
    const idTeam = event?.idTeam;
    const awayTeam = match?.awayTeam?.id;
    const homeTeam = match?.homeTeam?.id;
    if (event) {
      if (idTeam === homeTeam) {
        ret = 'home';
      } else if (idTeam === awayTeam) {
        ret = 'away';
      }
      if (event.eventType === LbEventType.GOAL && event.eventSubType === LbEventSubType.OWN) {
        ret = ret === 'away' ? 'home' : 'away';
      }
    }
  }
  return ret;
}

/**
 * Get post reporter
 */
export function getPostReporter(post: LbPost, liveblogData: LiveblogData) {
  const { author, parts, liveblogPostData } = post;

  if (getPostTypes(parts, liveblogPostData).includes(PartType.POLL) || liveblogPostData?.isSticky) return;

  const cleanedAuthor = author?.name?.replace('eom:', '')?.trim();

  if (liveblogPostData?.postAmbassador) {
    const starReporter = liveblogData?.starReporters?.find(
      (x) => x.person?.trim() === liveblogPostData?.postAmbassador?.trim(),
    );
    if (starReporter) starReporter.type = 'star';
    return starReporter;
  }

  let reporter = liveblogData?.homeReporters?.find((x) => x.id?.trim() === cleanedAuthor);
  let reporterType: LiveblogReporter['type'] = 'home';

  if (!reporter) {
    reporter = liveblogData?.awayReporters?.find((x) => x.id?.trim() === cleanedAuthor);
    reporterType = 'away';
  }
  if (!reporter) {
    reporter = liveblogData?.neutralReporters?.find((x) => x.id?.trim() === cleanedAuthor);
    reporterType = 'neutral';
  }

  if (reporter) {
    reporter.type = reporterType;
  }
  return reporter;
}

export function getSocialType(parts: LbPart[]) {
  const data = parts.find((part) => part.datasource.toLowerCase() === 'social')?.data;
  return data?.Type.toLowerCase();
}

export class D3JsonPollingService implements BasePollingService {
  private _nextPage = 0;
  private _endReached = false;
  private _match: Match;

  options: LbOptions;

  constructor(match: Match) {
    this._match = match;
  }
  getUpdates() {
    return Promise.resolve({ items: [] });
  }

  async loadPosts() {
    if (this._endReached) return { items: [] };
    const nextPageSegment = this._nextPage > 0 ? `_${this._nextPage}` : '';

    let result: { hasNext: boolean; items: LbPost[] };
    try {
      result = await loadFromAPIs(
        `/LiveBlogs/${this._match.competition.code.toLowerCase()}/${this._match.id}${nextPageSegment}.json`,
        {},
        { apiUrl: uefaBaseUrl },
      );
    } catch {
      this._endReached = true;
      return { items: [] };
    }

    if (result.hasNext) this._nextPage += 1;
    else this._endReached = true;

    return { items: result.items };
  }
}
